import React from "react";
import { withRouter } from "react-router";
import { Table } from "antd";
import { showColumnAction } from "./RegFormColumn";
import "./RegFormTable.css";
import { numberWithCommas } from "utils/GlobalFunctions";
import { getColumnConfig } from "./columnConfig";

function RegFormTable(props) {
  const { dataList, setVisible, setUserData, deleteData, sport_type, columnPreferences } = props;

  if (dataList === undefined) return <div />;

  // Get column configuration
  const columnConfig = getColumnConfig(sport_type);

  // Get user preferences or use defaults
  const prefs = columnPreferences?.columns || {};

  // Helper function to calculate column width based on header text
  const calculateColumnWidth = (text, minWidth = 80) => {
    // Approximate 10 pixels per character + 48px padding for sorter icon
    const calculatedWidth = Math.max(text.length * 10 + 48, minWidth);
    return calculatedWidth;
  };

  // Build columns array based on preferences
  let columns = [];

  Object.keys(columnConfig).forEach((key) => {
    const config = columnConfig[key];
    const pref = prefs[key] || { visible: config.defaultVisible };

    // Skip if not visible (unless locked)
    if (!pref.visible && !config.locked) return;

    // Calculate width: use user-set preference if available, otherwise auto-calculate from label
    // Auto-calculate if width is the default width (hasn't been manually adjusted)
    const columnWidth = (pref.width && pref.width !== config.defaultWidth)
      ? pref.width
      : calculateColumnWidth(config.label);

    // Build column definition
    let columnDef = {
      title: config.label,
      dataIndex: key,
      width: columnWidth,
      key: key
    };

    // Handle specific columns with custom rendering
    switch (key) {
      case "ID":
        columnDef.fixed = "left";
        columnDef.sorter = (a, b) => parseInt(a.ID) - parseInt(b.ID);
        break;

      case "immun_verified":
        columnDef.align = "center";
        columnDef.sorter = (a, b) => {
          const aVal = a.immun_verified === 1 || a.immun_verified === "1" || a.immun_verified === true || a.immun_verified === "true";
          const bVal = b.immun_verified === 1 || b.immun_verified === "1" || b.immun_verified === true || b.immun_verified === "true";
          return (aVal === bVal) ? 0 : aVal ? -1 : 1;
        };
        columnDef.render = (iv, info) => {
          return (
            <input
              type="checkbox"
              checked={info.immun_verified === 1 || info.immun_verified === "1" || info.immun_verified === true || info.immun_verified === "true"}
              disabled
              style={{ width: 18, height: 18 }}
            />
          );
        };
        break;

      case "camper_first_name":
        columnDef.fixed = "left";
        columnDef.sorter = (a, b) => `${a.camper_first_name}`.localeCompare(`${b.camper_first_name}`);
        break;

      case "camper_last_name":
        columnDef.fixed = "left";
        columnDef.sorter = (a, b) => `${a.camper_last_name}`.localeCompare(`${b.camper_last_name}`);
        break;

      case "first_name":
        columnDef.fixed = "left";
        columnDef.sorter = (a, b) => `${a.first_name}`.localeCompare(`${b.first_name}`);
        break;

      case "last_name":
        columnDef.fixed = "left";
        columnDef.sorter = (a, b) => `${a.last_name}`.localeCompare(`${b.last_name}`);
        break;

      case "camper_age":
        columnDef.sorter = (a, b) => `${a.camper_age}`.localeCompare(`${b.camper_age}`);
        break;

      case "camper_grade":
        columnDef.sorter = (a, b) => parseInt(a.camper_grade) - parseInt(b.camper_grade);
        columnDef.render = (info) => {
          return info == 0 ? "K" : info;
        };
        break;

      case "grade":
        columnDef.sorter = (a, b) => parseInt(a.grade) - parseInt(b.grade);
        columnDef.render = (info) => {
          return info == 0 ? "K" : info;
        };
        break;

      case "camper_birthday":
      case "birthday":
        columnDef.sorter = (a, b) => `${a[key]}`.localeCompare(`${b[key]}`);
        columnDef.render = (info) => {
          var formattedTime = "";
          if (info !== undefined && (info != null) & (info != "")) {
            let year = info.split("-")[0];
            let month = info.split("-")[1];
            let day = info.split("-")[2];
            formattedTime = month + "/" + day + "/" + year.substr(-2);
          }
          return formattedTime;
        };
        break;

      case "reg_date":
        columnDef.sorter = (a, b) => a.reg_date - b.reg_date;
        columnDef.render = (record) => {
          return record?.replaceAll("-", "/");
        };
        break;

      case "team_name":
        columnDef.sorter = (a, b) => `${a.team_name}`.localeCompare(`${b.team_name}`);
        break;

      case "total_checkout":
        columnDef.sorter = (a, b) => parseFloat(a.total_checkout) - parseFloat(b.total_checkout);
        columnDef.render = (info) => {
          return "$" + numberWithCommas(info);
        };
        break;

      case "processing_fee":
        columnDef.sorter = (a, b) => parseFloat(a.processing_fee) - parseFloat(b.processing_fee);
        columnDef.render = (info) => {
          return "$" + numberWithCommas(info);
        };
        break;

      case "fee":
        columnDef.sorter = (a, b) => parseFloat(a.fee) - parseFloat(b.fee);
        columnDef.render = (info) => {
          return "$" + numberWithCommas(info);
        };
        break;

      case "payment_method":
        columnDef.sorter = (a, b) => `${a.payment_method}`.localeCompare(`${b.payment_method}`);
        break;

      case "payment_status":
        columnDef.sorter = (a, b) => `${a.payment_status}`.localeCompare(`${b.payment_status}`);
        break;

      case "gender":
      case "camper_gender":
        columnDef.sorter = (a, b) => `${a[key]}`.localeCompare(`${b[key]}`);
        break;

      case "fee_label":
        columnDef.sorter = (a, b) => `${a.fee_label}`.localeCompare(`${b.fee_label}`);
        break;

      case "height":
        columnDef.sorter = (a, b) => parseFloat(a.height) - parseFloat(b.height);
        break;

      case "experience":
        columnDef.sorter = (a, b) => `${a.experience}`.localeCompare(`${b.experience}`);
        break;

      case "coaching":
        columnDef.sorter = (a, b) => `${a.coaching}`.localeCompare(`${b.coaching}`);
        break;

      case "discount_code":
        columnDef.sorter = (a, b) => `${a.discount_code}`.localeCompare(`${b.discount_code}`);
        break;

      case "action":
        columnDef.fixed = "right";
        columnDef.render = (op, info) => {
          return showColumnAction(info, setVisible, setUserData, deleteData);
        };
        break;

      default:
        // Default string sorting for other columns
        if (typeof dataList[0]?.[key] === "string") {
          columnDef.sorter = (a, b) => `${a[key] || ""}`.localeCompare(`${b[key] || ""}`);
        }
        break;
    }

    columns.push(columnDef);
  });

  const dataSource =
    dataList.length == 0
      ? []
      : dataList.map((data, index) => {
          return { ...data, key: index };
        });

  return (
    <div className="info-list-widget">
      <Table
        columns={columns}
        dataSource={dataSource}
        rowClassName={(record, index) =>
          index % 2 === 0 ? "table-row-light" : "table-row-dark"
        }
        scroll={{ x: 900 }}
        pagination={{ pageSize: 10 }}
      />
    </div>
  );
}

export default withRouter(RegFormTable);
